import { Injectable } from '@angular/core';

/**
 * GeocoderService provides Google Maps geocoding functionality.
 * Lazy-loads the Google Maps Geocoder library and caches the instance.
 */
@Injectable({
  providedIn: 'root'
})
export class GeocoderService {
  private geocoder: google.maps.Geocoder | null = null;

  /**
   * Lazy-loads the Google Maps Geocoder library.
   * Caches the geocoder instance after first load.
   */
  private async loadGeocoder(): Promise<google.maps.Geocoder> {
    if (this.geocoder) {
      return this.geocoder;
    }

    const [{ Geocoder }] = await Promise.all([
      google.maps.importLibrary('geocoding') as Promise<google.maps.GeocodingLibrary>
    ]);

    this.geocoder = new Geocoder();
    return this.geocoder;
  }

  /**
   * Reverse geocodes coordinates to get the closest city name.
   * Searches for locality, neighborhood, political, or administrative_area_level_3.
   * @returns City name string or null on failure.
   */
  async getClosestCity(lat: number, lon: number): Promise<string | null> {
    try {
      const geocoder = await this.loadGeocoder();

      return new Promise((resolve) => {
        geocoder.geocode({ location: { lat, lng: lon } }, (results, status) => {
          if (status === 'OK' && results?.length) {
            const addressComponents = results[0].address_components;
            const cityComponent =
              addressComponents.find((c) => c.types.includes('locality')) ||
              addressComponents.find((c) => c.types.includes('neighborhood')) ||
              addressComponents.find((c) => c.types.includes('political')) ||
              addressComponents.find((c) => c.types.includes('administrative_area_level_3'));

            resolve(cityComponent ? cityComponent.long_name : null);
          } else {
            console.warn('Geocoding failed:', status);
            resolve(null);
          }
        });
      });
    } catch (error) {
      console.error('Error in getClosestCity:', error);
      return null;
    }
  }

  /**
   * Reverse geocodes coordinates to get the formatted address.
   * Handles Plus Code format as shown in mockups.
   * @returns Formatted address string or null on failure.
   */
  async getClosestAddress(lat: number, lon: number): Promise<string | null> {
    try {
      const geocoder = await this.loadGeocoder();

      return new Promise((resolve) => {
        geocoder.geocode({ location: { lat, lng: lon } }, (results, status) => {
          if (status === 'OK' && results?.length) {
            resolve(results[0].formatted_address);
          } else {
            console.warn('Geocoding failed:', status);
            resolve(null);
          }
        });
      });
    } catch (error) {
      console.error('Error in getClosestAddress:', error);
      return null;
    }
  }

  /**
   * Extracts state abbreviation from geocoding results.
   * @returns Two-letter state code or null.
   */
  async getState(lat: number, lon: number): Promise<string | null> {
    try {
      const geocoder = await this.loadGeocoder();

      return new Promise((resolve) => {
        geocoder.geocode({ location: { lat, lng: lon } }, (results, status) => {
          if (status === 'OK' && results?.length) {
            const addressComponents = results[0].address_components;
            const stateComponent = addressComponents.find((c) =>
              c.types.includes('administrative_area_level_1')
            );
            resolve(stateComponent ? stateComponent.short_name : null);
          } else {
            resolve(null);
          }
        });
      });
    } catch (error) {
      console.error('Error in getState:', error);
      return null;
    }
  }
}
